/// <amd-module name="TFS/Token/PatTokenPage/Context/TokenContext" />
import { TokenActionCreator, TokenFilter } from 'Actions/TokenActionCreator';
import { IAccountNameContextValue } from 'Context/AccountNameContext';
import { IPatPageContextValue, PatPageConsumer } from 'Context/PatPageContext';
import { DisplayFilterOptions, SortByOptions } from 'Contracts/Token';
import { TokenConfig } from 'Models/TokenConfig';
import { Token } from 'Models/Token';
import { ISelectionOptions, Selection, SelectionMode } from "OfficeFabric/Selection";
import { IVssPageContext } from "VSS/Platform/Context";
import { ListSelection } from "VSSUI/List";
import * as React from "react";

export interface ITokenContextValue {
    tokens?: Array<Token>,
    selectedFilter: TokenFilter,
    selectedSort: SortByOptions,
    isDescending: boolean,
    selectedToken?: Token,
    canLoadMoreTokens: boolean,
    tokenSelection: Selection;
    onSelectedTokenChange: (token?: Token) => void,
    serverErrorMessage: string,
    listSelection: ListSelection,
    selectedOrgFilter?: string,
    organizations?: IAccountNameContextValue,
    selectedOrg: string,
}

const defaultTokenContextValue: ITokenContextValue = {
    selectedFilter: { status: DisplayFilterOptions.Active, organization: undefined },
    isDescending: true,
    selectedSort: SortByOptions.DisplayName,
    canLoadMoreTokens: true,
    tokenSelection: new Selection(),
    onSelectedTokenChange: () => undefined as void,
    serverErrorMessage: "",
    listSelection: new ListSelection({ multiSelect: false }),
    selectedOrg: "",
}

const TokenContext = React.createContext<ITokenContextValue>(undefined as never);

export interface ITokenProviderProps {
}

export interface ITokenProviderInternalState {
    tokens?: Array<Token>,
    selectedFilter?: TokenFilter,
    selectedSort?: SortByOptions,
    isDescending?: boolean,
    selectedToken?: Token,
    fetchVersion?: number;
    canLoadMoreTokens?: boolean,
    serverErrorMessage?: string,
    listSelection: ListSelection,
    selectedOrg?: string,
}

export interface ITokenProviderInternalProps extends ITokenProviderProps {
    tokenAction: TokenActionCreator;
    tokenConfig: TokenConfig;
    pageContext: IVssPageContext;
}

// Exported for testing purposes
export class TokenProviderInternal extends React.Component<ITokenProviderInternalProps, ITokenProviderInternalState> {
    private tokenSelection = new Selection({
        selectionMode: SelectionMode.single,
        onSelectionChanged: () => {
            const { tokens } = this.state;
            const selectedTokens = this.tokenSelection.getSelection() as Token[];
            if (tokens && selectedTokens && selectedTokens.length > 0) {
                const selectedToken = selectedTokens[0];
                this.setState({
                    selectedToken,
                });
            } else {
                this.setState({
                    selectedToken: undefined,
                });
            }
        },
        canSelectItem: ((item) => {
            const token = item as Token;
            return token && token.isValid;
        })
    } as ISelectionOptions);

    constructor(props: ITokenProviderInternalProps) {
        super(props);
        this.addAllListeners(props);
        this.state = {
            fetchVersion: 0,
            selectedFilter: { status: DisplayFilterOptions.Active, organization: undefined },
            selectedSort: SortByOptions.Status,
            isDescending: true,
            canLoadMoreTokens: true,
            listSelection: new ListSelection({ multiSelect: false }),
        }
    }

    public render() {
        const tokenContextValue: ITokenContextValue = {
            ...defaultTokenContextValue,
            ...this.state,
            tokenSelection: this.tokenSelection,
            onSelectedTokenChange: this.onSelectedTokenChange,
        } as ITokenContextValue;
        return (
            <TokenContext.Provider value={tokenContextValue}>
                {this.props.children}
            </TokenContext.Provider>
        );
    }

    private readonly onSelectedTokenChange = (token: Token) => {
        this.setState({ selectedToken: token });
    }

    private addAllListeners = (newProps: ITokenProviderInternalProps) => {
        const { tokenAction } = newProps;
        const { startedTokenFetch, finishedTokenFetch, recievedNewPageOfTokens, receivedErrorFromServer } = tokenAction.dispatchers;
        startedTokenFetch.addListener((payload) => {
            const { fetchVersion, filter, sortBy, isDescending } = payload;
            this.setState({
                fetchVersion,
                selectedFilter: filter,
                selectedSort: sortBy,
                isDescending,
                tokens: undefined,
            })
        });
        finishedTokenFetch.addListener((payLoad) => {
            const { fetchVersion, tokens, nextRowNumber, numberTokensBeforeFilter } = payLoad;
            this.setTokenList(fetchVersion, tokens, nextRowNumber, numberTokensBeforeFilter);
        });
        recievedNewPageOfTokens.addListener((payLoad) => {
            const { fetchVersion, tokens, nextRowNumber, numberTokensBeforeFilter } = payLoad;
            this.addTokensToList(fetchVersion, tokens, nextRowNumber, numberTokensBeforeFilter);
        });
        receivedErrorFromServer.addListener((payLoad) => {
            const { errorMessage } = payLoad;
            this.setServerErrorMessage(errorMessage);
        });
    }

    private setServerErrorMessage = (errorMessage: string) => {
        this.setState(() => {
            return {
                serverErrorMessage: errorMessage
            };
        });
    }

    private setTokenList = (fetchVersion: number, sessionTokens: Token[], nextRowNumber: number, numberTokensBeforeFilter: number) => {
        this.setState((prevState) => {
            // set the fetch version on the set, not the add
            if (!prevState.fetchVersion || fetchVersion >= prevState.fetchVersion) {
                this.tokenSelection.setItems(sessionTokens.map((token) => {
                    return {
                        ...token,
                        key: token.authorizationId
                    };
                }));

                return {
                    tokens: sessionTokens,
                    canLoadMoreTokens: numberTokensBeforeFilter === this.props.tokenConfig.tokenPageData.getTokenPageSize && nextRowNumber !== 0,
                };
            }

            return {};
        });
    }

    private addTokensToList = (fetchVersion: number, sessionTokens: Token[], nextRowNumber: number, numberTokensBeforeFilter: number) => {
        this.setState((prevState) => {
            if (!prevState.fetchVersion || fetchVersion >= prevState.fetchVersion) {
                let newTokens = (prevState.tokens || []).concat(sessionTokens);
                this.tokenSelection.setItems(newTokens.map((token) => {
                    return {
                        ...token,
                        key: token.authorizationId
                    }
                }));

                // Preserve the currently selected token while new ones are added
                if (prevState.selectedToken) {
                    this.tokenSelection.setKeySelected(prevState.selectedToken.authorizationId, true, false);
                }

                // Note that `selectedToken: prevState.selectedToken` cannot be used as `setItems` would reset it
                return {
                    tokens: newTokens,
                    canLoadMoreTokens: numberTokensBeforeFilter === this.props.tokenConfig.tokenPageData.getTokenPageSize && nextRowNumber !== 0,
                };
            }

            return {};
        });
    }
}

export const TokenProvider = (props: ITokenProviderProps) =>
    <PatPageConsumer>
        {(patPageContext: IPatPageContextValue) =>
            <TokenProviderInternal
                {...props}
                pageContext={patPageContext.pageContext}
                tokenAction={TokenActionCreator.getInstance(patPageContext.pageContext)}
                tokenConfig={TokenConfig.getInstance(patPageContext.pageContext)}
            />}
    </PatPageConsumer>

export const TokenConsumer = TokenContext.Consumer;
